import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    UserCell,
} from "@athena/ui";
import { AddUserToOrganizationDocument, UsersDocument } from "../../graphql/users.generated";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

/**
 * How many candidates one search shows.
 *
 * A picker, not a directory: somebody adding a named person types their name. Narrowing the search
 * is the way to find someone beyond this, which is what the search box is for.
 */
const CANDIDATE_LIMIT = 50;

interface AddOrgMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * The organization being added to. Also excludes its existing members from the picker, in the
     * query rather than here — the alternative is reading every member's id into the browser to
     * filter against, which is the roster this page stopped fetching.
     */
    organizationId: string;
    /**
     * When set, only members of this organization are eligible candidates — used to add users to a
     * sub-org, where an eligible user must already belong to its parent. Omit to allow any user in
     * the system (top-level org).
     */
    restrictToOrganizationId?: string;
    /** Called after all selected users have been added, so the caller can refetch the member list. */
    onAdded: () => void;
}

/** Adds one or more existing users to an organization (or sub-organization — a sub-org is just an org) as USER-role members. */
export function AddOrgMemberDialog({
    open,
    onOpenChange,
    organizationId,
    restrictToOrganizationId,
    onAdded,
}: AddOrgMemberDialogProps) {
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        selectedUserIds,
        setSelectedUserIds,
    ] = useState<Set<string>>(new Set());
    const [
        adding,
        setAdding,
    ] = useState<boolean>(false);
    /*
     * Searched in the database.
     *
     * This asked for the first 200 users and filtered them here, which made the search quietly
     * wrong rather than slow: anybody outside those 200 — of 85,371 — could not be found, and the
     * dialog said "No users found" as though they did not exist.
     *
     * Eligibility and exclusion move with it. `excludeOrganizationId` drops people who are already
     * members; `organizationId` on the filter restricts a sub-org's candidates to its parent's
     * members. Both were sets built from arrays this page no longer fetches.
     */
    const settledSearch = useDebouncedValue(search);
    const { data, loading } = useQuery(UsersDocument, {
        variables: {
            filter: {
                search: settledSearch || undefined,
                organizationId: restrictToOrganizationId,
                excludeOrganizationId: organizationId,
            },
            limit: CANDIDATE_LIMIT,
        },
        skip: !open,
    });
    const [addUserToOrganization] = useMutation(AddUserToOrganizationDocument);

    const filteredUsers = data?.users ?? [];

    const closeDialog = () => {
        onOpenChange(false);
        setSearch("");
        setSelectedUserIds(new Set());
    };

    const toggleUser = (userId: string) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const handleAdd = async () => {
        if (selectedUserIds.size === 0) return;
        setAdding(true);
        try {
            await Promise.all(
                [...selectedUserIds].map((userId) =>
                    addUserToOrganization({ variables: { input: { userId, organizationId, role: "USER" } } }),
                ),
            );
            onAdded();
            closeDialog();
        } finally {
            setAdding(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (nextOpen) onOpenChange(true);
                else closeDialog();
            }}
        >
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Add users</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    <Input
                        placeholder="Search by name or email…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                        type="text"
                    />
                    <div className="flex max-h-60 flex-col overflow-y-auto rounded-md border">
                        {loading && (
                            <div className="text-muted-foreground px-3 py-4 text-center text-xs">Searching…</div>
                        )}
                        {!loading && filteredUsers.length === 0 && (
                            <div className="text-muted-foreground px-3 py-4 text-center text-xs">
                                {restrictToOrganizationId
                                    ? "No eligible users — a user must belong to the parent organization first."
                                    : "No users found"}
                            </div>
                        )}
                        {filteredUsers.map((u) => {
                            const selected = selectedUserIds.has(u.id);
                            return (
                                <label
                                    key={u.id}
                                    className="hover:bg-muted flex cursor-pointer items-center gap-3 border-b px-3 py-2.5 text-left text-sm last:border-b-0"
                                >
                                    <Checkbox checked={selected} onCheckedChange={() => toggleUser(u.id)} />
                                    <UserCell id={u.id} firstName={u.firstName} lastName={u.lastName} email={u.email} />
                                </label>
                            );
                        })}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={closeDialog}>
                        Cancel
                    </Button>
                    <Button disabled={selectedUserIds.size === 0 || adding} onClick={() => void handleAdd()}>
                        {adding
                            ? "Adding…"
                            : selectedUserIds.size > 0
                              ? `Add ${selectedUserIds.size} user${selectedUserIds.size > 1 ? "s" : ""}`
                              : "Add"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
